import { Tabs as TabsPrimitive } from "@base-ui/react/tabs";
import { useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Chip } from "../chip";
import { Icon, type IconName } from "../icon";

// ============================================================================
// TabsList
// ============================================================================

const tabsListStyles = [
  "flex",
  "flex-row",
  "items-center",
  "border-b",
  "border-outline-variant",
].join(" ");

export interface TabsListProps
  extends Omit<TabsPrimitive.List.Props, "className"> {
  /** Adds horizontal content inset while keeping the tab-list border full width. */
  inset?: boolean;
}

function TabsList({ children, inset = false, ...props }: TabsListProps) {
  const { onScroll, ref, ...listProps } = props;
  const listRef = useRef<HTMLDivElement>(null);
  const updateOverflowStateRef = useRef(() => {});
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);

  updateOverflowStateRef.current = function updateOverflowState() {
    const list = listRef.current;

    if (!list) {
      setShowLeftGradient(false);
      setShowRightGradient(false);
      return;
    }

    const hasOverflow = list.scrollWidth > list.clientWidth;
    setShowLeftGradient(hasOverflow && list.scrollLeft > 0);
    setShowRightGradient(
      hasOverflow && list.scrollLeft + list.clientWidth < list.scrollWidth - 1,
    );
  };

  function setListRef(element: HTMLDivElement | null) {
    listRef.current = element;

    if (typeof ref === "function") {
      ref(element);
    } else if (ref) {
      ref.current = element;
    }
  }

  useLayoutEffect(() => {
    updateOverflowStateRef.current();
  });

  useLayoutEffect(() => {
    const list = listRef.current;

    if (!list || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => {
      updateOverflowStateRef.current();
    });
    observer.observe(list);
    for (const tab of list.children) {
      observer.observe(tab);
    }
    const mutationObserver = new MutationObserver(() => {
      for (const tab of list.children) {
        observer.observe(tab);
      }
      updateOverflowStateRef.current();
    });
    mutationObserver.observe(list, { childList: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  function handleScroll(
    event: Parameters<NonNullable<TabsPrimitive.List.Props["onScroll"]>>[0],
  ) {
    updateOverflowStateRef.current();
    onScroll?.(event);
  }

  return (
    <div className="relative">
      <TabsPrimitive.List
        {...listProps}
        ref={setListRef}
        onScroll={handleScroll}
        className={cn(
          tabsListStyles,
          "overflow-x-auto",
          inset && "px-(--spacing-24)",
        )}
      >
        {children}
      </TabsPrimitive.List>
      {showLeftGradient && (
        <div
          aria-hidden="true"
          data-slot="tabs-overflow-gradient-left"
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-[24px] bg-linear-to-r from-surface to-transparent"
        />
      )}
      {showRightGradient && (
        <div
          aria-hidden="true"
          data-slot="tabs-overflow-gradient-right"
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-[24px] bg-linear-to-l from-surface to-transparent"
        />
      )}
    </div>
  );
}

// ============================================================================
// TabsTrigger
// ============================================================================

const tabsTriggerBase = [
  // Layout
  "inline-flex",
  "flex-row",
  "items-center",
  "gap-(--spacing-8)",
  "px-(--spacing-12)",
  "py-(--spacing-6)",
  "rounded-tl-(--radius-sm)",
  "rounded-tr-(--radius-sm)",
  // Typography base
  "font-(family-name:--font-family-body)",
  "text-(length:--font-size-body-large)",
  "leading-(--font-line-height-24)",
  "tracking-(--font-letter-spacing-default)",
  "whitespace-nowrap",
  "outline-none",
  "cursor-pointer",
  "select-none",
  "relative",
  // Transitions
  "transition-colors",
  "duration-200",
  "ease-in-out",
  // Hover state (pointer devices only, not on disabled)
  "not-data-[disabled]:hover:bg-surface-hover",
  // Pressed state
  "not-data-[disabled]:active:bg-surface-pressed",
  // Focus ring
  "focus-visible:ring-2",
  "focus-visible:ring-button-focus-ring",
  // Disabled state
  "data-[disabled]:opacity-50",
  "data-[disabled]:pointer-events-none",
  "data-[disabled]:cursor-default",
].join(" ");

// Selected state via data-active attribute (set by Base UI on the active tab).
// The indicator is an absolutely-positioned ::after pseudo-element that sits
// below the trigger, overlaying the TabsList's 1px border without affecting
// the trigger's background or height.
const tabsTriggerStateStyles = [
  "text-text-subtext",
  "after:absolute",
  "after:bottom-0",
  "after:left-0",
  "after:right-0",
  "after:h-(--spacing-2)",
  "after:translate-y-px",
  "after:bg-transparent",
  "after:transition-colors",
  "after:duration-200",
  "after:ease-in-out",
  "group",
  "data-[active]:text-text-default",
  "data-[active]:after:bg-primary",
].join(" ");

export interface TabsTriggerProps
  extends Omit<TabsPrimitive.Tab.Props, "className"> {
  /**
   * Optional leading icon displayed to the left of the label.
   * When the icon communicates an alert or warning, use an accessibility/
   * negative semantic icon — not an error-red icon, which is reserved for
   * destructive actions and form validation errors.
   */
  icon?: IconName;
  /**
   * Optional trailing chip label. Keep to ≤3 characters
   * (e.g. "12", "99+", "New"). Do not use purely for decoration.
   *
   * Design guideline: max 6 tabs per Tabs instance.
   */
  chipLabel?: string | number;
  /** Augment the tab's accessible label when chipLabel carries meaningful
   * information for screen reader users (e.g. "Alerts, 12 unread"). */
  "aria-label"?: string;
  children: React.ReactNode;
}

function TabsTrigger({
  icon,
  chipLabel,
  children,
  ...props
}: TabsTriggerProps) {
  return (
    <TabsPrimitive.Tab
      className={cn(tabsTriggerBase, tabsTriggerStateStyles)}
      {...props}
    >
      {icon && <Icon name={icon} size="sm" tone="inherit" />}
      <span
        data-text={typeof children === "string" ? children : undefined}
        className="relative flex flex-col items-center group-data-[active]:font-semibold before:content-[attr(data-text)] before:font-semibold before:invisible before:h-0 before:overflow-hidden before:pointer-events-none"
      >
        {children}
      </span>
      {chipLabel != null && (
        <Chip tone="neutral" label={chipLabel} aria-hidden />
      )}
    </TabsPrimitive.Tab>
  );
}

// ============================================================================
// TabsContent
// ============================================================================

export interface TabsContentProps
  extends Omit<TabsPrimitive.Panel.Props, "className"> {}

function TabsContent({ children, ...props }: TabsContentProps) {
  return (
    <TabsPrimitive.Panel className="outline-none" {...props}>
      {children}
    </TabsPrimitive.Panel>
  );
}

// ============================================================================
// Tabs (root)
// ============================================================================

export interface TabsProps
  extends Omit<TabsPrimitive.Root.Props, "className"> {}

/**
 * Tabs component for organizing content into switchable panels.
 *
 * Built on `@base-ui/react/tabs`. Keyboard navigation (arrow keys, Home/End,
 * Enter/Space) and ARIA roles are handled automatically by the base primitive.
 *
 * Design guidelines:
 * - Maximum 6 tabs per instance
 * - Chip labels should be ≤3 characters
 *
 * @example
 * <Tabs defaultValue="overview">
 *   <TabsList>
 *     <TabsTrigger value="overview">Overview</TabsTrigger>
 *     <TabsTrigger value="alerts" icon={faCircleExclamation} chipLabel="12">
 *       Alerts
 *     </TabsTrigger>
 *     <TabsTrigger value="settings" disabled>Settings</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="overview">...</TabsContent>
 *   <TabsContent value="alerts">...</TabsContent>
 *   <TabsContent value="settings">...</TabsContent>
 * </Tabs>
 */
function Tabs({ children, ...props }: TabsProps) {
  return <TabsPrimitive.Root {...props}>{children}</TabsPrimitive.Root>;
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
